<template>
  <component
    :is="componentType"
    :type="href ? null : type"
    :class="[classes, 'relative']"
    :disabled="disabled || processing"
    :href="href"
    :target="target"
    :rel="rel"
  >
    <span
      class="inline-flex min-w-0 items-center"
      :class="{ invisible: props.processing }"
    >
      <div
        v-if="slots['leadingIcon']"
        :class="[leadingIconMargin, 'flex items-center']"
      >
        <slot name="leadingIcon" />
      </div>
      <component
        :is="props.leadingIcon"
        v-if="props.leadingIcon"
        :class="[
          leadingIconMargin,
          iconSizeClasses(buttonSizeToIconSizeMap(props.size)),
        ]"
      />
      <slot />
      <component
        :is="props.trailingIcon"
        v-if="props.trailingIcon"
        :class="[
          trailingIconMargin,
          iconSizeClasses(buttonSizeToIconSizeMap(props.size)),
        ]"
      />
    </span>
    <span
      v-if="props.processing"
      class="absolute inset-0 flex items-center justify-center"
    >
      <LoadingSpinner
        :color="loadingSpinnerColor"
        :size="buttonSizeToIconSizeMap(props.size)"
      />
    </span>
  </component>
</template>

<script setup>
import { Link } from '@inertiajs/vue3'
import { computed, useSlots } from 'vue'
import LoadingSpinner from '../LoadingSpinner.vue'
import { buttonSizeToIconSizeMap, iconSizeClasses } from '../common'
import { useButtonClasses } from './common'

const props = defineProps({
  type: {
    type: String,
    default: 'button',
  },
  href: {
    type: String,
    default: null,
  },
  size: {
    type: String,
    default: 'lg',
  },
  color: {
    type: String,
    default: 'default',
  },
  shape: {
    type: String,
    default: 'default',
  },
  rounded: {
    type: String,
    default: null,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  active: Boolean,
  external: Boolean,
  noBlank: Boolean,
  leadingIcon: Function,
  trailingIcon: Function,
})

const slots = useSlots()

const classes = useButtonClasses(props)

const componentType = computed(() => {
  if (props.external) return 'a'

  return props.href && !props.disabled ? Link : 'button'
})

const loadingSpinnerColor = computed(() => {
  if (props.color !== 'default') return 'text-gray-600 dark:text-white'
  else return 'text-white'
})

const leadingIconMargin = computed(() => {
  // Check if default slot is empty
  if (!slots['default']) return ''

  if (props.shape === 'rounded' || props.shape === 'circular') {
    return (
      {
        xs: '-ml-0.5 mr-0.5',
        sm: 'mr-1',
        md: 'mr-1',
        lg: 'mr-1.5',
        xl: 'mr-1.5',
      }[props.size] ?? 'mr-1.5'
    )
  } else {
    return (
      {
        xs: 'mr-1',
        sm: '-ml-0.5 mr-2',
        md: '-ml-1 mr-2',
        lg: '-ml-1 mr-3',
        xl: '-ml-1 mr-3',
      }[props.size] ?? 'mr-2 -ml-1'
    )
  }
})

const trailingIconMargin = computed(() => {
  // Check if default slot is empty
  if (!slots['default']) return ''

  if (props.shape === 'rounded' || props.shape === 'circular') {
    return (
      {
        xs: '-mr-0.5 ml-0.5',
        sm: 'ml-1',
        md: 'ml-1',
        lg: 'ml-1.5',
        xl: 'ml-1.5',
      }[props.size] ?? 'ml-1.5'
    )
  } else {
    return (
      {
        xs: 'ml-1',
        sm: '-mr-0.5 ml-2',
        md: '-mr-1 ml-2',
        lg: '-mr-1 ml-3',
        xl: '-mr-1 ml-3',
      }[props.size] ?? 'ml-2 -mr-1'
    )
  }
})

const target = computed(() =>
  props.external && !props.noBlank ? '_blank' : null
)
const rel = computed(() => (props.external ? 'noopener noreferrer' : null))
</script>
